import { useState, useContext, useEffect } from "react";
import { ClientContext } from "../../../contexts/clientProvider";
import { AnexosContext } from "../../../contexts/anexosProvider";
import { UserContext } from "../../../contexts/userProvider";
import { BiGlobe, BiCheck } from "react-icons/bi";
import BaseSelect from "../../General/FormsComp/Select/BaseSelect";

const SelecaoGlobalEquipamentos = ({
  equipamentos,
  onAplicarSelecaoGlobal,
  marcas,
  modelos,
}) => {
  const { clientes } = useContext(ClientContext);
  const { anexosByClient } = useContext(AnexosContext);
  const { setAlert } = useContext(UserContext);
  
  const [selecaoGlobal, setSelecaoGlobal] = useState({
    cliente: '',
    anexo: '',
    marca: '',
    modelo: ''
  });
  
  const [newModelos, setNewModelos] = useState([]);
  const [newMarcas, setNewMarcas] = useState([]);
  const [newClientes, setNewClientes] = useState([]);
  const [newAnexos, setNewAnexos] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);


  useEffect(() => {
    let newMarcas = marcas ? marcas : [];
    setNewMarcas(newMarcas);
  }, [marcas]);

  useEffect(() => {
    let newModelos = modelos ? modelos : [];
    setNewModelos(newModelos);
  }, [modelos]);

  useEffect(() => {
    let newClientes = clientes ? clientes : [];
    setNewClientes(newClientes);
  }, [clientes]);

  // Buscar anexos quando um cliente for selecionado
  useEffect(() => {
    const buscarAnexos = async () => {
      if (selecaoGlobal.cliente) {
        try {
          const anexos = await anexosByClient(selecaoGlobal.cliente);
          setNewAnexos(anexos || []);
        } catch (error) {
          console.error('Erro ao buscar anexos:', error);
          setNewAnexos([]);
        }
      } else {
        setNewAnexos([]);
      }
    };

    buscarAnexos();
  }, [selecaoGlobal.cliente, anexosByClient]);

  const handleSelectChange = (field) => (e) => {
    const { value } = e.target;
    const newValue = parseInt(value) || '';
    
    setSelecaoGlobal(prevSelecao => {
      const novaSelecao = { ...prevSelecao, [field]: newValue };
      
      // Lógica especial para relacionamentos
      if (field === 'marca') {
        // Limpar modelo se marca mudou
        if (newValue !== prevSelecao.marca) {
          novaSelecao.modelo = '';
        }
      }
      
      if (field === 'modelo') {
        const modelo = newModelos.find(m => m.id === newValue);
        if (modelo) {
          novaSelecao.marca = modelo.id_marca;
        }
      }
      
      if (field === 'cliente') {
        // Limpar anexo quando cliente mudar
        novaSelecao.anexo = '';
      }
      
      return novaSelecao;
    });
  };

  const handleAplicarSelecao = () => {
    // Verificar se pelo menos um campo está selecionado
    const camposPreenchidos = Object.values(selecaoGlobal).filter(value => value !== '').length;
    
    if (camposPreenchidos === 0) {
      setAlert({
        visible: true,
        title: "Atenção!",
        placeholder: "Selecione pelo menos um campo para aplicar globalmente.",
        confirm: false,
        type: "warning"
      });
      return;
    }

    // Confirmar aplicação
    const camposTexto = [];
    if (selecaoGlobal.cliente) camposTexto.push(`Cliente: ${newClientes.find(c => c.id === selecaoGlobal.cliente)?.nome}`);
    if (selecaoGlobal.anexo) camposTexto.push(`Anexo: ${newAnexos.find(a => a.id === selecaoGlobal.anexo)?.numeroanexo}`);
    if (selecaoGlobal.marca) camposTexto.push(`Marca: ${newMarcas.find(m => m.id === selecaoGlobal.marca)?.nome}`);
    if (selecaoGlobal.modelo) camposTexto.push(`Modelo: ${newModelos.find(m => m.id === selecaoGlobal.modelo)?.nome}`);

    setAlert({
      type: 'confirm',
      placeholder: `Aplicar seleção global para todos os ${equipamentos.length} equipamentos?\n\n${camposTexto.join('\n')}`,
      visible: true,
      confirm: true,
      title: 'Confirmar Seleção Global',
      callback: () => {
        onAplicarSelecaoGlobal(selecaoGlobal);
        setSelecaoGlobal({
          cliente: '',
          anexo: '',
          marca: '',
          modelo: ''
        });
        setIsExpanded(false);
      }
    });
  };

  const handleLimparSelecao = () => {
    setSelecaoGlobal({
      cliente: '',
      anexo: '',
      marca: '',
      modelo: ''
    });
  };

  // Filtrar modelos pela marca selecionada
  const modelosFiltrados = selecaoGlobal.marca 
    ? newModelos.filter(modelo => modelo.id_marca === selecaoGlobal.marca)
    : newModelos;

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-4 border border-blue-200 dark:border-blue-800">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <BiGlobe className="w-5 h-5 mr-2 text-blue-600" />
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
            Seleção Global para Equipamentos
          </h3>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors duration-200"
        >
          {isExpanded ? 'Ocultar' : 'Expandir'}
        </button>
      </div>
      
      <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
        Aplique valores comuns a todos os {equipamentos.length} equipamentos de uma vez
      </p>

      {isExpanded && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Cliente */}
            <div>
              <BaseSelect
                label="Cliente Global"
                name="cliente_global"
                value={selecaoGlobal.cliente}
                onChange={handleSelectChange('cliente')}
                options={newClientes || []}
                placeholder="Selecione o cliente"
                valueKey="id"
                labelKey="nome"
              />
            </div>

            {/* Anexo */}
            <div>
              <BaseSelect
                label="Anexo Global"
                name="anexo_global"
                value={selecaoGlobal.anexo}
                onChange={handleSelectChange('anexo')}
                options={newAnexos || []}
                placeholder={!selecaoGlobal.cliente ? "Selecione cliente primeiro" : "Selecione o anexo"}
                valueKey="id"
                labelKey="numeroanexo"
                disabled={!selecaoGlobal.cliente}
              />
            </div>

            {/* Marca */}
            <div>
              <BaseSelect
                label="Marca Global"
                name="marca_global"
                value={selecaoGlobal.marca}
                onChange={handleSelectChange('marca')}
                options={newMarcas || []}
                placeholder="Selecione a marca"
                valueKey="id"
                labelKey="nome"
              />
            </div>

            {/* Modelo */}
            <div>
              <BaseSelect
                label="Modelo Global"
                name="modelo_global"
                value={selecaoGlobal.modelo}
                onChange={handleSelectChange('modelo')}
                options={modelosFiltrados || []}
                placeholder={!selecaoGlobal.marca ? "Selecione marca primeiro" : "Selecione o modelo"}
                valueKey="id"
                labelKey="nome"
                disabled={!selecaoGlobal.marca}
              />
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end space-x-3 pt-3 border-t border-blue-200 dark:border-blue-700">
            <button
              onClick={handleLimparSelecao}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-200"
            >
              Limpar
            </button>
            <button
              onClick={handleAplicarSelecao}
              className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200"
            >
              <BiCheck className="w-5 h-5 mr-1" />
              Aplicar a Todos
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelecaoGlobalEquipamentos;
